// What the agent was OFFERED, in the words of whoever wrote the tool.
//
// Every other instrument here infers meaning from what a call looks like, and
// every one of those inferences has degenerated into string matching — a mutates
// flag keyed on program names, an environment keyed on the word "prod", a reach
// keyed on whether a host was localhost. All of them are proxies for a semantic
// question, all of them fail silently at the boundary, and all of them encode this
// tenant's conventions.
//
// A tool definition does not need inferring. The server author wrote down what the
// act does, in prose, and it ships in every request body:
//
//   mcp__…__slack_send_message
//     "Sends a message to a Slack channel or user. …"
//   mcp__…__slack_read_channel
//     "Reads messages from a Slack channel … Read-only. …"
//
// That is better evidence than anything a classifier of mine produces, it costs
// nothing, and it is written in the vocabulary of the system being acted on rather
// than of the shell. Read it and hand it to the judge.
//
// The other half is the gap between offered and called. A capability an agent had
// and never used is either dead weight or a routing tell — and when the same act
// shows up through another door, it is the second. That pairing is how this
// pipeline learned that a Kubernetes MCP server sat unused while the same work
// went through `kubectl`.

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();

/**
 * @param {object[]} bodies  {toolDefs:[{name,description,schema}], sessionId, calls:[{name}]}
 * @returns catalog of every capability offered, with what it says it does and
 *          whether anyone reached for it.
 */
export function capabilityCatalog(bodies) {
  const tools = new Map();

  for (const b of bodies || []) {
    const sid = b.sessionId || "";
    for (const d of b.toolDefs || []) {
      const name = d?.name;
      if (!name) continue;
      let t = tools.get(name);
      if (!t) {
        t = {
          name,
          server: name.startsWith("mcp__") ? name.split("__")[1] : null,
          description: clean(d.description),
          // The parameter names are the author's own account of what the act
          // takes — the same evidence as the description, in schema form.
          params: Object.keys(d.schema?.properties || d.schema || {}).slice(0, 12),
          offeredIn: new Set(), calledIn: new Set(), calls: 0,
        };
        tools.set(name, t);
      }
      // First non-empty description wins; some bodies carry a truncated one.
      if (!t.description && d.description) t.description = clean(d.description);
      t.offeredIn.add(sid);
    }
    for (const c of b.calls || []) {
      const t = tools.get(c.name);
      if (!t) continue;
      t.calls += 1;
      t.calledIn.add(sid);
    }
  }

  const list = [...tools.values()].map((t) => ({
    ...t,
    offeredIn: t.offeredIn.size,
    calledIn: t.calledIn.size,
    // Self-declared read-only, when the author says so. NOT inferred from the
    // verb: `get_issue` may still write an audit record, and only the author
    // knows.
    declaredReadOnly: /\bread[- ]only\b/i.test(t.description),
  }));

  return {
    tools: list.sort((a, b) => b.calls - a.calls || b.offeredIn - a.offeredIn),
    /** Offered and never reached for. Dead weight, or the losing door. */
    unused: () => list.filter((t) => t.offeredIn > 0 && t.calls === 0),
    /** Per MCP server, since a server is the unit someone decides to connect. */
    servers: () => {
      const by = new Map();
      for (const t of list) {
        if (!t.server) continue;
        let s = by.get(t.server);
        if (!s) { s = { server: t.server, tools: 0, used: 0, calls: 0, offeredIn: 0 }; by.set(t.server, s); }
        s.tools += 1;
        s.used += t.calls > 0 ? 1 : 0;
        s.calls += t.calls;
        s.offeredIn = Math.max(s.offeredIn, t.offeredIn);
      }
      return [...by.values()].sort((a, b) => b.calls - a.calls);
    },
  };
}
